import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';
import { ArrowRight, ShieldCheck, Globe, Radio, ChevronRight } from 'lucide-react';

const products = [
  {
    name: 'Mushin Factory OS',
    tagline: 'The operating system for factory operations',
    description: 'Mushin Factory OS digitizes plant workflows: quality execution, standards management, complaints/CAPA/8D, end-to-end traceability, APQP/PPAP, incoming quality, training, calibration, 4M change management, and audit types.',
    path: '/products/factory-os',
    icon: ShieldCheck,
    modules: ['Quality Execution', 'Standards Mgmt', 'Complaints / CAPA / 8D', 'End-to-End Traceability', 'APQP / PPAP', 'IQC / Incoming Quality', 'Production & Maintenance', 'Calibration Lab', 'Training / DOJO', 'Stores / FIFO', '4M Change Mgmt', '5 Audit Types', 'Safety', 'ERP Integration'],
    color: '#C41E3A',
  },
  {
    name: 'Mushin Vendor Connect',
    tagline: 'Supplier quality management per VDA 6.3 and IATF expectations',
    description: 'Mushin Vendor Connect governs supplier quality workflows: onboarding, audits, complaints, CAPA, certificates, scorecards, RFQ flows, and compliance tracking across your vendor ecosystem.',
    path: '/products/vendor-connect',
    icon: Globe,
    modules: ['Supplier Onboarding', 'Profile Management', 'Certificate Management', 'Supplier Complaints & CAPA', 'Supplier Audits', 'RFQ / LOI Flows', 'Supplier Scorecards', 'Compliance Visibility'],
    color: '#6B7280',
  },
  {
    name: 'IoT & Machine Data',
    tagline: 'IoT for shopfloor machine and process visibility',
    description: 'The IoT layer within Mushin Factory OS captures machine and process data from PLCs, sensors, and gateways. Real-time OEE, cycle time, downtime analytics, process parameters, and Andon views for production intelligence.',
    path: '/products/factory-os',
    icon: Radio,
    modules: ['PLC / Sensor Connectivity', 'Machine Status', 'Production Counts', 'Cycle Time & OEE', 'Downtime Analytics', 'Process Parameters', 'Andon Views', 'Alarm History'],
    color: '#374151',
  },
];

function Section({ children, className = '' }) {
  const [ref, inView] = useInView();
  return <section ref={ref} className={`${inView ? 'animate-fade-in-up' : 'opacity-0'} ${className}`}>{children}</section>;
}

export default function ProductsPage() {
  return (
    <div className="pt-16">
      {/* Hero */}
      <section className="relative py-24 lg:py-32 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">The Mushin Platform</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-[#111827]" data-testid="products-title">
            Two products.<br />One connected factory platform.
          </h1>
          <p className="mt-6 text-lg text-[#374151] max-w-2xl">
            ERP records the business. Mushin controls the plant. Quality execution, supplier governance, and IoT machine visibility for manufacturing.
          </p>
        </div>
      </section>

      {/* Platform Architecture Visual */}
      <Section className="py-16 border-t border-[#F0F0F0] bg-[#FAFAFA]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium text-center">Platform Architecture</p>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-[#111827] text-center mb-10">How the products connect</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="bg-white border-2 border-[#C41E3A]/20 rounded-xl p-6 shadow-sm">
              <ShieldCheck className="w-8 h-8 text-[#C41E3A] mx-auto mb-3" strokeWidth={1.5} />
              <div className="text-sm font-bold text-[#111827] font-heading">Mushin Factory OS</div>
              <div className="text-[10px] text-[#374151] mt-1">Plant quality & execution</div>
              <div className="mt-3 text-[9px] text-[#6B7280]">Quality workflows, traceability, CAPA, standards</div>
            </div>
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 shadow-sm">
              <Globe className="w-8 h-8 text-[#6B7280] mx-auto mb-3" strokeWidth={1.5} />
              <div className="text-sm font-bold text-[#111827] font-heading">Mushin Vendor Connect</div>
              <div className="text-[10px] text-[#374151] mt-1">Supplier quality & governance</div>
              <div className="mt-3 text-[9px] text-[#6B7280]">VDA 6.3 audits, complaints, scorecards, certificates</div>
            </div>
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 shadow-sm">
              <Radio className="w-8 h-8 text-[#374151] mx-auto mb-3" strokeWidth={1.5} />
              <div className="text-sm font-bold text-[#111827] font-heading">IoT & Machine Data</div>
              <div className="text-[10px] text-[#374151] mt-1">IoT machine visibility</div>
              <div className="mt-3 text-[9px] text-[#6B7280]">OEE, cycle time, downtime, process parameters</div>
            </div>
          </div>
          <div className="flex items-center justify-center mt-6 gap-6">
            <div className="h-px flex-1 max-w-[140px] bg-gradient-to-r from-transparent to-[#C41E3A]/30" />
            <div className="px-4 py-2 bg-white border border-[#E5E7EB] rounded-full text-[10px] font-mono text-[#6B7280] uppercase tracking-wider shadow-sm">
              Shared Data + Linked Workflows
            </div>
            <div className="h-px flex-1 max-w-[140px] bg-gradient-to-l from-transparent to-[#C41E3A]/30" />
          </div>
        </div>
      </Section>

      {/* Product Detail Sections */}
      {products.map((product, i) => (
        <Section key={product.name} className={`py-20 lg:py-28 border-t border-[#F0F0F0] ${i % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <div className="w-2 h-12 rounded-full mb-6" style={{ backgroundColor: product.color }} />
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-[#111827]" data-testid={`product-title-${i}`}>{product.name}</h2>
                <p className="text-sm text-[#C41E3A] font-medium mt-2">{product.tagline}</p>
                <p className="mt-6 text-[#374151] leading-relaxed">{product.description}</p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link to={product.path} className="bg-[#C41E3A] hover:bg-[#A11830] text-white px-6 py-2.5 rounded-full text-[15px] font-medium transition-colors inline-flex items-center gap-2" data-testid={`product-learn-${i}`}>
                    Explore {product.name.split(' ').pop()} <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a href="mailto:sales@mushinlabs.com" className="border border-[#D1D5DB] hover:border-[#9CA3AF] text-[#374151] px-6 py-2.5 rounded-full text-[15px] font-medium transition-colors" data-testid={`product-quote-${i}`}>Get a Quote</a>
                </div>
              </div>
              <div>
                <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-[#6B7280] mb-4">Key Modules</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {product.modules.map((mod) => (
                    <div key={mod} className="flex items-center gap-3 bg-white border border-[#E5E7EB] rounded-lg px-4 py-3 shadow-sm">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: product.color }} />
                      <span className="text-[15px] text-[#374151]">{mod}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Section>
      ))}

      {/* CTA */}
      <section className="py-24 text-center border-t border-[#F0F0F0] bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-heading text-3xl font-bold text-[#111827]">Need help choosing?</h2>
          <p className="mt-4 text-[#374151]">Our team can help you identify the right starting point for your automotive plant.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="bg-[#C41E3A] hover:bg-[#A11830] text-white px-7 py-3 rounded-full text-[15px] font-medium transition-colors" data-testid="products-cta-demo">Book a Demo</Link>
            <Link to="/packages" className="border border-[#D1D5DB] hover:border-[#9CA3AF] text-[#374151] px-7 py-3 rounded-full text-[15px] font-medium transition-colors" data-testid="products-cta-packages">View Packages</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
